import React from 'react';
import { Link } from 'react-router-dom';
import { isCustomScriptJob } from './scheduledJobActions';

function parseSchedulerConfig(row) {
  if (!row?.config) return {};
  if (typeof row.config === 'object') return row.config;
  try {
    const parsed = JSON.parse(row.config);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Primary in-app link for a scheduled job row (record view, workflow setup, etc.).
 * @returns {string|null}
 */
export function resolveScheduledJobLink(row) {
  if (!row) return null;
  if (row.link_href) return String(row.link_href);

  const config = parseSchedulerConfig(row);
  const actionType =
    row.action_type != null ? String(row.action_type).trim().toLowerCase() : '';
  const schedulerType =
    row.scheduler_type != null ? String(row.scheduler_type).trim().toLowerCase() : '';

  if (
    actionType === 'execute_workflow' ||
    schedulerType === 'workflow'
  ) {
    const workflowId = config.workflowId ?? config.workflow_id;
    if (workflowId) {
      return `/setup/workflows/${encodeURIComponent(String(workflowId).trim())}`;
    }
    return null;
  }

  if (isCustomScriptJob(row)) {
    const objectName =
      row.object_name != null ? String(row.object_name).trim() : '';
    const recordId = config.recordId ?? row.parent_id;
    if (
      objectName &&
      recordId &&
      objectName.toLowerCase() !== 'scheduler'
    ) {
      return `/view/${encodeURIComponent(objectName)}/${encodeURIComponent(String(recordId).trim())}`;
    }
    if (config.customScriptId || config.custom_script_id) {
      return '/setup/custom-scripts';
    }
    return null;
  }

  const parentId = row.parent_id;
  const objectName =
    row.object_name != null ? String(row.object_name).trim() : '';
  if (!parentId || !objectName) {
    const recordId = config.recordId;
    const configObject = config.objectName ?? config.object_name;
    if (recordId && configObject) {
      return `/view/${encodeURIComponent(String(configObject).trim())}/${encodeURIComponent(String(recordId).trim())}`;
    }
    return null;
  }

  const obj = objectName.toLowerCase();
  if (obj === 'email_campaign') {
    return `/view/email_campaign/${encodeURIComponent(String(parentId).trim())}`;
  }

  return `/view/${encodeURIComponent(objectName)}/${encodeURIComponent(String(parentId).trim())}`;
}

export function ScheduledJobNameLink({ row, className = '' }) {
  const label = row?.display_name || row?.job_name || row?.execution_label || row?.id || '—';
  const href = resolveScheduledJobLink(row);
  if (!href) {
    return <span className={className || 'fw-semibold text-break'}>{label}</span>;
  }
  return (
    <Link to={href} className={`${className || 'fw-semibold text-break'} link-primary text-decoration-none`}>
      {label}
    </Link>
  );
}
